import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/toast";
import {
  api,
  ApiError,
  type CaseDetail,
  type CaseVersion,
  type CreateVersionPromptPreview,
  type StartFromStep,
} from "@/lib/api";
import { cn } from "@/lib/utils";

const START_FROM_OPTIONS: Array<{
  value: StartFromStep;
  label: string;
  description: string;
}> = [
  {
    value: "generate_phone",
    label: "generate_phone",
    description:
      "保留剧本 + 元信息；从 generate_phone 重跑流水线。手机运行时自动 pin 到当前最新版。",
  },
  {
    value: "refine_phone",
    label: "refine_phone",
    description: "保留手机 JSON 草稿；从 refine_phone 重跑校验修复。",
  },
  {
    value: "experience_guide",
    label: "线索指南",
    description: "保留手机 JSON；从线索指南重跑。",
  },
  {
    value: "asset_plan",
    label: "asset_plan",
    description: "保留手机 + 向导；从 asset_plan 重跑素材步骤。",
  },
  {
    value: "head_refs",
    label: "head_refs",
    description: "保留素材计划；重跑 head_refs 与 gen_assets。",
  },
  {
    value: "gen_assets",
    label: "gen_assets",
    description: "保留头像参考；仅重跑 gen_assets。",
  },
  {
    value: "review",
    label: "review",
    description:
      "保留素材与 phone JSON；仅重跑线索审阅（提取图片内容 + 对照剧本，不改 JSON）。",
  },
  {
    value: "enrich",
    label: "enrich",
    description:
      "保留审阅后的 phone JSON 与素材；从 enrich 补日常数据，再重跑 asset_plan → gen_assets。",
  },
];

const selectClassName = cn(
  "h-8 w-full rounded-md border border-input bg-input/20 px-2 py-1 text-sm outline-none",
  "focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/30",
  "disabled:cursor-not-allowed disabled:opacity-50 dark:bg-input/30",
);

type WizardStep = 1 | 2 | 3;

function versionEligible(
  version: CaseVersion,
  startFrom: StartFromStep,
): boolean {
  const steps = new Set(version.succeededSteps ?? []);
  switch (startFrom) {
    case "generate_phone":
      return (
        Boolean(version.cleanedScriptR2Key) && Boolean(version.meta)
      );
    case "refine_phone":
      return (
        steps.has("generate_phone") ||
        steps.has("build_phone") ||
        Boolean(version.phoneJsonR2Key)
      );
    case "experience_guide":
      return (
        steps.has("refine_phone") ||
        steps.has("generate_phone") ||
        steps.has("build_phone") ||
        Boolean(version.phoneJsonR2Key)
      );
    case "asset_plan":
      return (
        steps.has("experience_guide") ||
        steps.has("refine_phone") ||
        steps.has("generate_phone") ||
        steps.has("build_phone") ||
        Boolean(version.phoneJsonR2Key)
      );
    case "head_refs":
      return steps.has("asset_plan");
    case "gen_assets":
      return steps.has("head_refs");
    case "review":
      return steps.has("gen_assets");
    case "enrich":
      return steps.has("review");
  }
}

function formatVersionOption(v: CaseVersion): string {
  const branch = v.branchLabel ? ` · ${v.branchLabel}` : "";
  return `${v.locale} v${v.versionN} · ${v.status}${branch}`;
}

function wizardLabel(step: WizardStep): string {
  if (step === 1) return "1 选择";
  if (step === 2) return "2 提示词";
  return "3 确认";
}

export function CreateVersionDialog({
  open,
  onOpenChange,
  caseId,
  detail,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  caseId: string;
  detail: CaseDetail;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [wizardStep, setWizardStep] = useState<WizardStep>(1);
  const [startFrom, setStartFrom] = useState<StartFromStep>("generate_phone");
  const [sourceVersionId, setSourceVersionId] = useState<string>("");
  const [branchLabel, setBranchLabel] = useState("");
  const [systemPrompt, setSystemPrompt] = useState("");
  const [userPrompt, setUserPrompt] = useState("");
  const [previewNote, setPreviewNote] = useState<string | null>(null);
  const [previewMeta, setPreviewMeta] = useState<
    CreateVersionPromptPreview["meta"] | undefined
  >(undefined);

  const eligibleVersions = useMemo(() => {
    return detail.versions.filter((v) => versionEligible(v, startFrom));
  }, [detail.versions, startFrom]);

  useEffect(() => {
    if (!open) return;
    setWizardStep(1);
    setStartFrom("generate_phone");
    setBranchLabel("");
    setSystemPrompt("");
    setUserPrompt("");
    setPreviewNote(null);
    setPreviewMeta(undefined);
    const first = detail.versions.find((v) =>
      versionEligible(v, "generate_phone"),
    );
    setSourceVersionId(first?.id ?? detail.versions[0]?.id ?? "");
  }, [open, detail.versions]);

  useEffect(() => {
    if (!eligibleVersions.some((v) => v.id === sourceVersionId)) {
      setSourceVersionId(eligibleVersions[0]?.id ?? "");
    }
  }, [eligibleVersions, sourceVersionId]);

  const previewQuery = useQuery({
    queryKey: [
      "create-version-preview",
      caseId,
      startFrom,
      sourceVersionId,
    ],
    queryFn: () =>
      api.previewCreateVersionPrompts(caseId, {
        startFrom,
        sourceVersionId: sourceVersionId || undefined,
      }),
    enabled: open && wizardStep === 2 && Boolean(sourceVersionId),
  });

  useEffect(() => {
    if (!previewQuery.data) return;
    setSystemPrompt(previewQuery.data.systemPrompt);
    setUserPrompt(previewQuery.data.userPrompt);
    setPreviewNote(previewQuery.data.note);
    setPreviewMeta(previewQuery.data.meta);
  }, [previewQuery.data]);

  const createMutation = useMutation({
    mutationFn: () =>
      api.createVersion(caseId, {
        startFrom,
        sourceVersionId: sourceVersionId || undefined,
        branchLabel: branchLabel.trim() || undefined,
        systemPrompt: systemPrompt.trim() || undefined,
        userPrompt: userPrompt.trim() || undefined,
      }),
    onSuccess: async (result) => {
      toast.add({
        type: "success",
        title: "版本已创建",
        description: `从 ${startFrom} 启动`,
      });
      await queryClient.invalidateQueries({ queryKey: ["case", caseId] });
      onOpenChange(false);
      void navigate({
        to: "/cases/$caseId/versions/$versionId/pipeline",
        params: { caseId, versionId: result.version.id },
      });
    },
    onError: (err) => {
      toast.add({
        type: "error",
        title: "创建失败",
        description: err instanceof ApiError ? err.code : "create_failed",
      });
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[min(90vh,52rem)] flex-col gap-4 overflow-hidden sm:max-w-3xl">
        <DialogHeader className="shrink-0">
          <DialogTitle>创建版本</DialogTitle>
          <DialogDescription>
            从源版本派生，并在选定步骤启动流水线。
            {wizardLabel(wizardStep)}
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {wizardStep === 1 ? (
            <div className="flex flex-col gap-4">
              <Field>
                <FieldLabel htmlFor="start-from">起始步骤</FieldLabel>
                <select
                  id="start-from"
                  className={selectClassName}
                  value={startFrom}
                  onChange={(e) =>
                    setStartFrom(e.target.value as StartFromStep)
                  }
                >
                  {START_FROM_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <FieldDescription>
                  {
                    START_FROM_OPTIONS.find((o) => o.value === startFrom)
                      ?.description
                  }
                </FieldDescription>
              </Field>
              <Field>
                <FieldLabel htmlFor="source-version">源版本</FieldLabel>
                <select
                  id="source-version"
                  className={selectClassName}
                  value={sourceVersionId}
                  onChange={(e) => setSourceVersionId(e.target.value)}
                >
                  {eligibleVersions.length === 0 ? (
                    <option value="">无符合条件的版本</option>
                  ) : (
                    eligibleVersions.map((v) => (
                      <option key={v.id} value={v.id}>
                        {formatVersionOption(v)}
                      </option>
                    ))
                  )}
                </select>
              </Field>
              <Field>
                <FieldLabel htmlFor="branch-label">版本备注</FieldLabel>
                <input
                  id="branch-label"
                  className={selectClassName}
                  value={branchLabel}
                  onChange={(e) => setBranchLabel(e.target.value)}
                  placeholder="可选，方便识别版本"
                />
              </Field>
            </div>
          ) : null}

          {wizardStep === 2 ? (
            <div className="flex flex-col gap-4">
              {previewQuery.isLoading ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Spinner /> 加载提示词…
                </div>
              ) : previewQuery.isError ? (
                <p className="text-sm text-destructive">
                  无法加载提示词预览。
                </p>
              ) : (
                <>
                  {previewNote ? (
                    <p className="text-xs text-muted-foreground">
                      {previewNote}
                    </p>
                  ) : null}
                  {previewMeta?.unitCount != null ? (
                    <p className="text-xs text-muted-foreground">
                      单元：{previewMeta.unitCount} {previewMeta.unitLabel}
                    </p>
                  ) : null}
                  <Field>
                    <FieldLabel htmlFor="system-prompt">
                      系统提示词
                    </FieldLabel>
                    <Textarea
                      id="system-prompt"
                      value={systemPrompt}
                      onChange={(e) => setSystemPrompt(e.target.value)}
                      rows={10}
                      className="max-h-64 min-h-40 overflow-y-auto font-mono text-xs field-sizing-fixed"
                    />
                  </Field>
                  <Field>
                    <FieldLabel htmlFor="user-prompt">用户提示词</FieldLabel>
                    <Textarea
                      id="user-prompt"
                      value={userPrompt}
                      onChange={(e) => setUserPrompt(e.target.value)}
                      rows={10}
                      className="max-h-64 min-h-40 overflow-y-auto font-mono text-xs field-sizing-fixed"
                    />
                  </Field>
                </>
              )}
            </div>
          ) : null}

          {wizardStep === 3 ? (
            <div className="flex flex-col gap-2 text-sm">
              <p>
                起始步骤 <strong>{startFrom}</strong>
              </p>
              <p className="text-muted-foreground">
                源版本：{" "}
                {eligibleVersions.find((v) => v.id === sourceVersionId)
                  ? formatVersionOption(
                      eligibleVersions.find((v) => v.id === sourceVersionId)!,
                    )
                  : sourceVersionId}
              </p>
              {branchLabel.trim() ? (
                <p className="text-muted-foreground">
                  备注：{branchLabel.trim()}
                </p>
              ) : null}
            </div>
          ) : null}
        </div>

        <DialogFooter className="shrink-0">
          {wizardStep > 1 ? (
            <Button
              type="button"
              variant="outline"
              onClick={() =>
                setWizardStep((s) => (s === 3 ? 2 : 1) as WizardStep)
              }
            >
              上一步
            </Button>
          ) : (
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              取消
            </Button>
          )}
          {wizardStep < 3 ? (
            <Button
              type="button"
              disabled={
                (wizardStep === 1 && !sourceVersionId) ||
                (wizardStep === 2 && previewQuery.isLoading)
              }
              onClick={() =>
                setWizardStep((s) => (s === 1 ? 2 : 3) as WizardStep)
              }
            >
              下一步
            </Button>
          ) : (
            <Button
              type="button"
              disabled={createMutation.isPending || !sourceVersionId}
              onClick={() => createMutation.mutate()}
            >
              {createMutation.isPending ? <Spinner /> : null}
              创建并启动
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
